import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { getCloudflareAuth } from 'config/cloudflareAuth.api';
import { CF_AUTH_COOKIE } from 'constants/api';
import { AUTH_PENDING_ACTION_ENUM } from 'constants/authentication';
import { useFeedbackModal, useSession } from 'providers';
import { SESSION_STATE } from 'providers/SessionProvider/sessionProvider.constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { DASHBOARD_ROUTE } from 'routes/constants';
import { getCookie } from 'utils/cookie';
import { logError } from 'utils/logError';
import { AWAITING_VALIDATION_FEEDBACK } from './constants';
import { useAuthModalError } from './useAuthModalError';

/**
 * Custom hook for handling Cloudflare authentication.
 *
 * @param {boolean} triggerLogin - Flag indicating whether to trigger the login process automatically. Default is true.
 * @param {Function} successCallback - Callback function to be executed upon successful authentication.
 *
 * @returns {Object} An object containing the loading state.
 * @property {boolean} loading - Flag indicating whether the authentication process is in progress.
 */
export const useCloudflareAuth = (
  triggerLogin = true,
  successCallback?: () => void,
): { loading: boolean } => {
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(false);
  const { displayAuthErrorModal } = useAuthModalError();
  const {
    sessionState,
    updateExpiresIn,
    updateFirebaseId,
    isCloudflareAutoLoginEnabled,
    toggleCloudflareAutoLogin,
    setSessionState,
  } = useSession();
  const { openFeedbackModal } = useFeedbackModal();
  const navigate = useNavigate();
  // verify if cloudflare cookie is available
  const cloudflareCookie = getCookie(CF_AUTH_COOKIE);

  const loginWithCloudflare = useCallback(async () => {
    try {
      const data = await getCloudflareAuth();

      if (data.pendingAction === AUTH_PENDING_ACTION_ENUM.AWAITING_VALIDATION) {
        openFeedbackModal(AWAITING_VALIDATION_FEEDBACK);
        return;
      }

      updateFirebaseId(data.firebaseId);
      updateExpiresIn(data.expirationDate.toString());
      setSessionState(SESSION_STATE.AUTHENTICATED);
      toggleCloudflareAutoLogin(true);
      if (pathname === '/') {
        navigate(DASHBOARD_ROUTE);
      }
      successCallback?.();
    } catch (error) {
      logError({ error });

      if (error instanceof AxiosError) {
        displayAuthErrorModal(error);
      } else {
        // show cloudflare auth failure
        openFeedbackModal({
          type: 'warning',
          title: 'Invalid Cloudflare Credentials',
          message: 'Please sign in manually',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [
    displayAuthErrorModal,
    navigate,
    openFeedbackModal,
    pathname,
    setSessionState,
    successCallback,
    updateExpiresIn,
    updateFirebaseId,
    toggleCloudflareAutoLogin,
  ]);

  useEffect(() => {
    if (
      sessionState !== SESSION_STATE.AUTHENTICATED &&
      triggerLogin &&
      isCloudflareAutoLoginEnabled &&
      cloudflareCookie
    ) {
      setLoading(true);
      void loginWithCloudflare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    triggerLogin,
    cloudflareCookie,
    isCloudflareAutoLoginEnabled,
    sessionState,
  ]);

  return { loading };
};
